import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";

export interface DispatchDriver {
  id: string;
  name: string;
  driverNo: string;
  driverType: string;
  pictureUrl: string | null;
  location: string | null;
  parsedLocation: string | null;
  locationDateTime: Date | null;
  latitude: number | null;
  longitude: number | null;
  milesLast7Days: number;
  homeCity: string | null;
  homeState: string | null;
  hoursOfService: number | null;
  isAssigned: boolean;
  tractorId: string | null;
  trailerId: string | null;
  // Extra fields returned by the dispatch endpoints (used by dispatch-finder) and optional
  rawHoursOfService?: { dsaUsa708Hours?: number | null };
  tractorNumber?: string | null;
  trailerNumber?: string | null;
  attachedTrailerNumbers?: string[];
  productQualifications?: string[];
  prequalifiedProductQualifications?: string[];
  // Road call on the driver's assigned tractor (TMT-synced, read-only)
  roadCallStatus?: string | null;
  roadCallNumber?: string | null;
  roadCallOpenedDate?: Date | string | null;
}

export interface DispatchTrailer {
  id: string;
  trailerNumber: string;
  equipmentCode: string | null;
  currentLocation: string | null;
  locationDateTime?: Date | null;
  latitude: number | null;
  longitude: number | null;
  terminal: string | null;
  inService: boolean;
  isAssigned: boolean;
  nextServiceDate: Date | null;
  // TMT-synced open road call (read-only)
  roadCallStatus?: string | null;
  roadCallNumber?: string | null;
  roadCallOpenedDate?: Date | string | null;
  // Extra fields returned by the dispatch endpoints (used by dispatch-finder)
  attachedTractorNumber?: string | null;
}

export interface DispatchTractor {
  id: string;
  tractorNumber: string;
  currentLocation: string | null;
  parsedLocation: string | null;
  locationDateTime: Date | null;
  latitude: number | null;
  longitude: number | null;
  terminal: string | null;
  inService: boolean;
  isAssigned: boolean;
  nextServiceDate: Date | null;
  // Extra fields returned by the dispatch endpoints (used by dispatch-finder)
  attachedTrailerNumbers?: string[];
  // TMT-synced open road call (read-only)
  roadCallStatus?: string | null;
  roadCallNumber?: string | null;
  roadCallOpenedDate?: Date | string | null;
}

export interface DispatchFinderFilters {
  driverStatus?: "assigned" | "unassigned" | "both";
  driverTypes?: string[];
  trailerStatus?: string;
  trailerTerminal?: string;
  trailerProduct?: string;
  tractorStatus?: string;
  tractorTerminal?: string;
}

export interface SchedulingTeamDriverAssignment {
  id: string;
  driverId: string;
  name: string;
  lastName: string;
  preferredName?: string | null;
  driverNo: string;
  picture: string | null;
  specialNote: string | null;
  cellphone: string | null;
  currentLocation: string | null;
  parsedLocation: string | null;
  autoTransmissionOnly: boolean;
  boardName: string | null;
  billToClientId: string | null;
  billToClientName: string | null;
  boardId: string | null;
  peopleNetId: string | null;
  tractorId: string | null;
  tractorNumber: string | null;
  /** True when the driver's tractor assignment is marked as a spare unit. */
  tractorIsSpare?: boolean;
  trailerId: string | null;
  trailerNumber: string | null;
  homeLocation: string | null;
  teamMembers: string[] | null;
  /** When the driver last left a scheduling group (unassigned list only). */
  dateUnassignedUtc?: string | null;
  productId: string | null;
  productName: string | null;
  lastAvatarSyncDate: string | null;
  doNotDispatch: boolean;
  driverType: string | null;
  productQualifications: string[];
  bannedClients?: string[];
}

export interface SchedulingTeamDispatchContact {
  name: string | null;
  type: string | null;
  phone: string | null;
  email: string | null;
}

export interface SchedulingTeamForAssignments {
  id: string;
  name: string;
  clientId: string | null;
  clientName: string | null;
  dispatchContacts: SchedulingTeamDispatchContact[] | null;
  drivers: SchedulingTeamDriverAssignment[];
}

/** Lightweight scheduling-team shape returned by GET /api/board/listAll. */
export interface SchedulingTeamOption {
  id: string;
  name: string;
  drivers?: {
    driverId: string;
    boardName?: string | null;
    boardId?: string | null;
    billToClientId?: string | null;
  }[];
}

/** A driver displaced by an equipment assignment (server may use PascalCase keys). */
export interface DisplacedDriver {
  name?: string;
  Name?: string;
  driverNo?: string;
  DriverNo?: string;
  reason?: string;
  Reason?: string;
}

export interface AssignItemsResult {
  displacedDrivers?: DisplacedDriver[];
}

/** Lightweight bill-to client option shape returned by GET /api/board/clients. */
export interface BillToClientOption {
  id: string;
  companyName: string;
  clientId?: string | null;
}

export interface DriverAssignment {
  driverId: string;
  driverName: string;
  driverNo: string;
  tractorId: string | null;
  tractorNumber: string | null;
  trailerId: string | null;
  trailerNumber: string | null;
}

export interface DriverTripCountDay {
  date: string;
  count: number;
  trips: {
    id: string;
    clientName: string | null;
    billToName: string | null;
    loadLocationName: string | null;
  }[];
}

@Injectable({
  providedIn: "root",
})
export class DispatchService {
  private baseUrl = "/api/dispatch";

  constructor(private http: HttpClient) {}

  /**
   * Get all dispatch data (drivers, trailers, tractors) in a single optimized call
   */
  getDispatchData(filters?: DispatchFinderFilters): Observable<{
    drivers: DispatchDriver[];
    trailers: DispatchTrailer[];
    tractors: DispatchTractor[];
  }> {
    return this.http.get<{
      drivers: DispatchDriver[];
      trailers: DispatchTrailer[];
      tractors: DispatchTractor[];
    }>(`${this.baseUrl}/data`, {
      params: this.buildParams(filters),
    });
  }

  /**
   * Get drivers with optional filters for dispatch finder
   */
  getDrivers(filters?: DispatchFinderFilters): Observable<DispatchDriver[]> {
    return this.http.get<DispatchDriver[]>(`${this.baseUrl}/drivers`, {
      params: this.buildParams(filters),
    });
  }

  /**
   * Get trailers with optional filters for dispatch finder
   */
  getTrailers(filters?: DispatchFinderFilters): Observable<DispatchTrailer[]> {
    return this.http.get<DispatchTrailer[]>(`${this.baseUrl}/trailers`, {
      params: this.buildParams(filters),
    });
  }

  /**
   * Get tractors with optional filters for dispatch finder
   */
  getTractors(filters?: DispatchFinderFilters): Observable<DispatchTractor[]> {
    return this.http.get<DispatchTractor[]>(`${this.baseUrl}/tractors`, {
      params: this.buildParams(filters),
    });
  }

  /**
   * Get only unassigned drivers (no tractor or trailer)
   */
  getUnassignedDrivers(): Observable<DispatchDriver[]> {
    return this.http.get<DispatchDriver[]>(`${this.baseUrl}/drivers/unassigned`);
  }

  /**
   * Get active drivers not assigned to any scheduling team
   */
  getDriversWithoutSchedulingTeam(): Observable<SchedulingTeamDriverAssignment[]> {
    return this.http.get<SchedulingTeamDriverAssignment[]>(
      `${this.baseUrl}/driversWithoutSchedulingTeam`
    );
  }

  /**
   * Get all current driver-tractor-trailer assignments
   */
  getDriverAssignments(): Observable<DriverAssignment[]> {
    return this.http.get<DriverAssignment[]>(`${this.baseUrl}/assignments`);
  }

  /**
   * Assign drivers to tractors and trailers
   */
  assignItems(
    driverIds: string[],
    tractorIds: string[],
    trailerIds: string[],
    dryRun = false
  ): Observable<AssignItemsResult> {
    return this.http.post<AssignItemsResult>(`${this.baseUrl}/assign`, {
      driverIds,
      tractorIds,
      trailerIds,
      dryRun,
    });
  }

  /**
   * Get all scheduling teams (with their boards)
   */
  getAllSchedulingTeams(): Observable<SchedulingTeamOption[]> {
    return this.http.get<SchedulingTeamOption[]>("/api/board/listAll");
  }

  /**
   * Get bill-to clients for scheduling team driver assignment
   */
  getBillToClients(): Observable<BillToClientOption[]> {
    return this.http.get<BillToClientOption[]>("/api/board/clients");
  }

  /**
   * Add a driver to a scheduling team
   */
  addDriverToSchedulingTeam(
    schedulingTeamId: string,
    driverId: string,
    boardName?: string | null,
    billToClientId?: string | null
  ): Observable<void> {
    return this.http.post<void>("/api/board/addDrivers", {
      schedulingTeamId,
      driverAssignments: [
        {
          driverId,
          boardName: boardName || null,
          billToClientId: billToClientId || null,
        },
      ],
    });
  }

  updateDriverInSchedulingTeam(
    schedulingTeamId: string,
    driverId: string,
    boardId?: string | null,
    billToClientId?: string | null
  ): Observable<void> {
    return this.http.patch<void>("/api/board/updateDriverAssignment", {
      schedulingTeamId,
      driverId,
      boardId: boardId || null,
      billToClientId: billToClientId || null,
    });
  }

  /**
   * Remove a driver from a scheduling team
   */
  removeDriverFromSchedulingTeam(schedulingTeamId: string, driverId: string): Observable<void> {
    return this.http.post<void>("/api/board/removeDrivers", {
      schedulingTeamId,
      driverIds: [driverId],
    });
  }

  /**
   * Get scheduling teams with their assigned drivers for driver assignments view
   */
  getSchedulingTeamsWithDrivers(): Observable<SchedulingTeamForAssignments[]> {
    return this.http.get<SchedulingTeamForAssignments[]>(
      `${this.baseUrl}/schedulingTeamsWithDrivers`
    );
  }

  /**
   * Get list of driver IDs that have time off on a specific date
   * Simplified method for filtering drivers by date
   */
  getDriversWithTimeOff(date: Date | string): Observable<string[]> {
    return this.http.post<string[]>(`${this.baseUrl}/driversWithTimeOff`, {
      date: this.formatDateOnly(date),
    });
  }

  /**
   * Get driver time off events for a date range
   * Returns a dictionary keyed by driver ID with array of time off events
   */
  getDriverTimeOffForDateRange(
    driverIds: string[],
    startDate: Date | string,
    endDate: Date | string
  ): Observable<{ [driverId: string]: { id: string; date: string; eventType: string }[] }> {
    return this.http.post<{
      [driverId: string]: { id: string; date: string; eventType: string }[];
    }>(`${this.baseUrl}/driverTimeOff`, {
      driverIds,
      startDate: this.formatDateOnly(startDate),
      endDate: this.formatDateOnly(endDate),
    });
  }

  /**
   * Get trip counts for drivers within a date range
   * Returns a dictionary keyed by driver ID with array of date/count pairs
   */
  getDriverTripCountsForDateRange(
    driverIds: string[],
    startDate: Date | string,
    endDate: Date | string
  ): Observable<{ [driverId: string]: DriverTripCountDay[] }> {
    return this.http.post<{ [driverId: string]: DriverTripCountDay[] }>(
      `${this.baseUrl}/driverTripCounts`,
      {
        driverIds,
        startDate: this.formatDateOnly(startDate),
        endDate: this.formatDateOnly(endDate),
      }
    );
  }

  /**
   * Get driver notes for a date range
   */
  getDriverAccidentsAndBreakdownsForDateRange(
    driverIds: string[],
    startDate: Date | string,
    endDate: Date | string
  ): Observable<{
    [driverId: string]: {
      id: string;
      type: string;
      note: string;
      status: string;
      date: string;
      accidentDate?: string;
      eta?: string;
      internalNotes?: string;
    }[];
  }> {
    return this.http.post<{
      [driverId: string]: {
        id: string;
        type: string;
        note: string;
        status: string;
        date: string;
        accidentDate?: string;
        eta?: string;
        internalNotes?: string;
      }[];
    }>(`${this.baseUrl}/driverAccidentsAndBreakdowns`, {
      driverIds,
      startDate: this.formatDateOnly(startDate),
      endDate: this.formatDateOnly(endDate),
    });
  }

  /**
   * Get open and resolved note counts for a list of drivers
   */
  getDriverOpenNoteCounts(driverIds: string[]): Observable<{ [driverId: string]: number }> {
    return this.http.post<{ [driverId: string]: number }>(`api/driver/openNoteCounts`, driverIds);
  }

  /** Get open "Driver Release" note counts for a list of drivers */
  getDriverOpenReleaseNoteCounts(driverIds: string[]): Observable<{ [driverId: string]: number }> {
    return this.http.post<{ [driverId: string]: number }>(
      `api/driver/openReleaseNoteCounts`,
      driverIds
    );
  }

  private buildParams(filters?: DispatchFinderFilters): any {
    if (!filters) return {};

    const params: any = {};

    if (filters.driverStatus) params.driverStatus = filters.driverStatus;
    if (filters.driverTypes?.length) params.driverTypes = filters.driverTypes.join(",");
    if (filters.trailerStatus) params.trailerStatus = filters.trailerStatus;
    if (filters.trailerTerminal) params.trailerTerminal = filters.trailerTerminal;
    if (filters.trailerProduct) params.trailerProduct = filters.trailerProduct;
    if (filters.tractorStatus) params.tractorStatus = filters.tractorStatus;
    if (filters.tractorTerminal) params.tractorTerminal = filters.tractorTerminal;

    return params;
  }

  /**
   * Format a date to UTC timestamp for API calls (PostgreSQL requires UTC)
   */
  private formatDateOnly(date: Date | string): string {
    if (typeof date === "string") {
      // Return just the date portion to avoid timezone-shifting
      return date.substring(0, 10);
    }
    // Use local date components to avoid UTC conversion shifting the date
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${date.getFullYear()}-${month}-${day}`;
  }
}
